import { EmbeddedElementConfig, GalleryController } from '@ingatlancom/stimulus-gallery';
import { Modal, Collapse } from 'bootstrap';
import { isBelowViewportSize, breakpoints } from '../../utils/viewport_size_handler';
import { createVideo } from '../../utils/create_video';

export default class extends GalleryController {
  static targets = [
    'imageIndex',
    'caption',
    'zoomLevel',
    'thumbnailToggle',
    'bannerContainer',
    'additionalTexts',
    'imageCount',
    'fullscreenElement',
    'fullscreenToggle',
    'zoomOutBtn',
    'zoomInBtn',
    'fullscreenThumbnailVideo',
  ];
  static outlets = ['banner', 'details--gallery', 'video'];
  static classes = ['hide'];

  static values = {
    captions: Array,
    images: Array,
    isFullscreenModeMobile: { type: Boolean, default: false },
  };
  ZOOM_DELTA = 0.4;

  connect() {
    super.connect();
    this.isInitialized = false;
    this.setupEventListeners();
    this.initThumbnailContainer();
    this.initBannerPositions();
    this.registerScrollToZoom();
  }

  disconnect() {
    super.disconnect();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    this.unregisterScrollToZoom();
  }

  pushEvent(event) {
    window.GTM.pushEvent(event.params.event);
  }

  // Event Listeners Setup
  setupEventListeners() {
    this.setupModalListeners();
    this.setupFullscreenListeners();
  }

  showBannersWithConfig(extraAttribute, extraAttributeValue) {
    const matchingBanners = this.bannerOutlets.filter(
      (bannerController) => bannerController.element.getAttribute(extraAttribute) === extraAttributeValue
    );

    matchingBanners.forEach((bannerController) => {
      if (bannerController && typeof bannerController.showBanner === 'function') {
        bannerController.showBanner();
      }
    });
    return matchingBanners;
  }

  setupModalListeners() {
    const fullScreenGallery = document.getElementById('fullScreenGallery');
    const mobileBannerBottomId = document
      .getElementById('fs-gallery-mobile-bottom')
      .getAttribute('data-banner-zone-id-value');

    if (!fullScreenGallery) {
      return;
    }

    fullScreenGallery.addEventListener('show.bs.modal', () => {
      if (!isBelowViewportSize(breakpoints.desktop)) {
        this.showBannersWithConfig('data-banner-viewport-value', 'desktop');
      }
      if (isBelowViewportSize(breakpoints.desktop)) {
        this.showBannersWithConfig('data-banner-zone-id-value', mobileBannerBottomId);
        this.bannerOutlets
          .filter((bannerController) => bannerController.element.classList.contains('mobile-metric'))
          .forEach((bannerController) => bannerController.showBanner());
      }
    });

    fullScreenGallery.addEventListener('shown.bs.modal', () => {
      this.animateMainImages();
      this.moveImageContainersToOriginalPosition();
      this.resetZoom();
      this.registerKeyboardNavigation();
    });

    fullScreenGallery.addEventListener('hide.bs.modal', () => {
      this.bannerOutlets.forEach((banner) => banner.clearBanner());
      this.resetZoom();
      this.unregisterKeyboardNavigation();
      if (this.hasVideoOutlet) {
        this.videoOutlet.pause();
      }
      if (this.isFullscreenModeMobileValue) {
        this.isFullscreenModeMobileValue = false;
        this.handleFullscreenChange();
      }
    });
  }

  setupFullscreenListeners() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
  }

  openModal(index) {
    this.switchToImage(index, true);
    this.hideMainImages(false);
    const modalElement = document.getElementById('fullScreenGallery');
    Modal.getOrCreateInstance(modalElement).show();
  }

  // Image Navigation
  afterMainImageSwitch() {
    this.updateImageIndex();
    this.updateCaption();
    this.scrollToActiveThumbnail();
    this.resetZoom();
    this.detailsGalleryOutlet.switchToImage(this.activeImageIndex);

    this.hideNextImageAfterBanner();
    this.handleFullscreenControlsElementsVisibility();
  }

  hideNextImageAfterBanner() {
    this.mainImageTargets[0].style.opacity = '1';
    this.mainImageTargets[2].style.opacity = '1';

    for (const banner of this.banners) {
      let mainImageIndexToHide = null;
      if (banner.position === this.activeImageIndex) {
        mainImageIndexToHide = 0;
      }

      if (banner.position === this.activeImageIndex + 1) {
        mainImageIndexToHide = 2;
      }

      if (mainImageIndexToHide !== null) {
        this.mainImageTargets[mainImageIndexToHide].style.opacity = '0';
        break;
      }
    }
  }

  updateImageIndex() {
    this.imageIndexTarget.innerText = this.activeImageIndex + 1;
  }

  updateCaption() {
    if (!this.hasCaptionTarget) {
      return;
    }

    this.captionTarget.innerText = this.captionsValue[this.activeImageIndex] ?? '';
    this.captionTarget.classList.toggle(this.hideClass, !this.captionsValue[this.activeImageIndex]);
  }

  scrollToActiveThumbnail() {
    if (!this.isInitialized) {
      this.isInitialized = true;
      return;
    }

    this.thumbnailTargets[this.activeImageIndex]?.scrollIntoView({
      inline: 'start',
      block: 'nearest',
      behavior: 'smooth',
    });
  }

  // Zoom Controls
  zoomIn() {
    this.changeZoom(this.ZOOM_DELTA);
  }

  zoomOut() {
    this.changeZoom(-this.ZOOM_DELTA);
  }

  updateZoomInBtnState(zoomLevel) {
    this.zoomInBtnTarget.disabled = zoomLevel === this.MAX_ZOOM_LEVEL;
  }

  updateZoomOutBtnState(zoomLevel) {
    this.zoomOutBtnTarget.disabled = zoomLevel === this.MIN_ZOOM_LEVEL;
  }

  changeZoom(zoomDelta) {
    if (
      (zoomDelta > 0 && this.zoomLevel >= this.MAX_ZOOM_LEVEL) ||
      (zoomDelta < 0 && this.zoomLevel <= this.MIN_ZOOM_LEVEL)
    ) {
      return;
    }

    let newZoomLevel = this.zoomLevel + zoomDelta;

    newZoomLevel = Math.max(this.MIN_ZOOM_LEVEL, Math.min(this.MAX_ZOOM_LEVEL, newZoomLevel));

    this.updateZoomInBtnState(newZoomLevel);
    this.updateZoomOutBtnState(newZoomLevel);
    this.onZoomLevelChange(newZoomLevel);
    this.applyZoom(newZoomLevel);
  }

  resetZoom() {
    this.zoomOutBtnTarget.disabled = true;
    this.zoomInBtnTarget.disabled = false;
    this.applyZoom(this.MIN_ZOOM_LEVEL);
  }

  onZoomLevelChange(zoomLevel) {
    const zoomPercentage = Math.round(100 + (zoomLevel - 1) * 25);
    this.updateZoomInBtnState(zoomLevel);
    this.updateZoomOutBtnState(zoomLevel);
    this.zoomLevelTarget.textContent = `${zoomPercentage}%`;

    if (!!document.fullscreenElement || this.isFullscreenModeMobileValue) {
      this.canSwipe = zoomLevel > 1;
    }
  }

  shouldCancelScrollToZoom(clientX, clientY, target) {
    const zoomableArea = target.closest('.zoomable');
    return !zoomableArea;
  }

  // Fullscreen Controls
  toggleFullscreen() {
    const fullScreenGalleryElement = this.fullscreenElementTarget;
    if (!fullScreenGalleryElement) {
      return;
    }

    if (!document.fullscreenElement) {
      fullScreenGalleryElement
        .requestFullscreen()
        .then(() => {
          this.handleFullscreenChange();
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      this.exitFullscreen();
    }
  }

  exitFullscreen() {
    if (!document.fullscreenElement) {
      return;
    }

    document
      .exitFullscreen()
      .then(() => {
        this.handleFullscreenChange();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  handleFullscreenChange = () => {
    const isFullscreen = !!document.fullscreenElement || this.isFullscreenModeMobileValue;
    const mobileBannerBottomContainer = document.getElementById('fs-gallery-mobile-banner-bottom');
    this.additionalTextsTarget.classList.toggle('d-none', isFullscreen);
    this.thumbnailToggleTarget.classList.toggle('d-none', isFullscreen);
    mobileBannerBottomContainer.classList.toggle('d-none', isFullscreen);
    this.setIsSwipeBlocked(isFullscreen);
    isFullscreen ? this.unregisterKeyboardNavigation() : this.registerKeyboardNavigation();
    const fullscreenToggleIcon = this.fullscreenToggleTarget.querySelector('svg');
    if (fullscreenToggleIcon) {
      fullscreenToggleIcon.outerHTML = this.updateFullscreenToggleIcon();
    }
    setTimeout(() => {
      this.moveImageContainersToOriginalPosition();
      this.resetZoom();
    }, 100);
  };

  updateFullscreenToggleIcon = () => {
    if (document.fullscreenElement || this.isFullscreenModeMobileValue) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 21v-3H3v-2h5v5zm10 0v-5h5v2h-3v3zM3 8V6h3V3h2v5zm13 0V3h2v3h3v2z"/></svg>`;
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 21v-5h2v3h3v2zm13 0v-2h3v-3h2v5zM3 8V3h5v2H5v3zm16 0V5h-3V3h5v5z"/></svg>`;
  };

  toggleMobileFullscreen = () => {
    this.isFullscreenModeMobileValue = !this.isFullscreenModeMobileValue;
    this.handleFullscreenChange();
  };

  // UI Helpers
  animateMainImages() {
    if (this.mainImageTargets[1].complete) {
      this.showMainImages(true);
      return;
    }

    this.mainImageTargets[1].addEventListener('load', () => this.showMainImages(true), { once: true });
  }

  onThumbnailClick(e) {
    super.onThumbnailClick(e);
    this.collapseThumbnails();
  }

  collapseThumbnails() {
    if (!isBelowViewportSize(breakpoints.desktop)) {
      return;
    }

    const thumbnailsElement = document.getElementById('collapseThumbnails');
    const isCollapsed = !thumbnailsElement.classList.contains('show');
    if (isCollapsed) {
      return;
    }

    const thumbnails = new Collapse(thumbnailsElement, { toggle: false });
    thumbnails.hide();
  }

  initThumbnailContainer() {
    const thumbnails = document.getElementById('collapseThumbnails');

    thumbnails.addEventListener('hidden.bs.collapse', () => this.handleThumbnailsVisibilityChanged());
    thumbnails.addEventListener('shown.bs.collapse', () => this.handleThumbnailsVisibilityChanged());

    if (!isBelowViewportSize(breakpoints.desktop)) {
      thumbnails.addEventListener('hide.bs.collapse', () => this.hideMainImages(false));
      thumbnails.addEventListener('show.bs.collapse', () => this.hideMainImages(false));
      return;
    }

    thumbnails.classList.remove('show');
    this.thumbnailToggleTarget.classList.add('collapsed');
    this.thumbnailToggleTarget.setAttribute('aria-expanded', false);
  }

  handleThumbnailsVisibilityChanged() {
    if (!isBelowViewportSize(breakpoints.desktop)) {
      this.showMainImages(true);
    }

    this.moveImageContainersToOriginalPosition();
    this.resetZoom();
  }

  // Fs Banners
  toggleElementVisibility(element, show) {
    element.classList.toggle('d-block', show);
    element.classList.toggle('d-none', !show);
  }

  toggleCaptionVisibility(show) {
    if (this.hasCaptionTarget) {
      this.captionTarget.classList.toggle('d-block', show);
      this.captionTarget.classList.toggle('d-none', !show);
    }
  }

  hideMobileBannerBottomAndCaption() {
    const mobileBannerBottom = document.getElementById('fs-gallery-mobile-bottom');
    this.toggleCaptionVisibility(false);
    this.toggleElementVisibility(mobileBannerBottom, false);
    this.toggleElementVisibility(this.imageCountTarget, false);
    const mobileBannerBottomContainer = document.getElementById('fs-gallery-mobile-banner-bottom');
    mobileBannerBottomContainer.classList.toggle('border-top-0');
  }

  showMobileBannerBottomAndCaption() {
    const mobileBannerBottom = document.getElementById('fs-gallery-mobile-bottom');
    this.toggleCaptionVisibility(true);
    this.toggleElementVisibility(mobileBannerBottom, true);
    this.toggleElementVisibility(this.imageCountTarget, true);
    const mobileBannerBottomContainer = document.getElementById('fs-gallery-mobile-banner-bottom');
    mobileBannerBottomContainer.classList.toggle('border-top-0');
  }

  initBannerPositions() {
    this.banners = this.bannerContainerTargets.map((banner) => ({
      id: banner.dataset.bannerZoneId,
      position: parseInt(banner.dataset.bannerPosition, 10),
      container: banner,
    }));
  }

  getEmbeddedElementConfigs() {
    if (!isBelowViewportSize(breakpoints.desktop)) {
      return [];
    }

    return (this.banners || []).map((banner) => {
      return {
        position: [banner.position - 1, banner.position],
        config: this.createBannerConfig(banner.container, banner.id),
      };
    });
  }

  createBannerConfig(container, bannerId) {
    const showFn = () => {
      this.showBannersWithConfig('data-banner-zone-id-value', bannerId);
      container.classList.add('d-flex');
      container.classList.remove('d-none');
      this.hideMobileBannerBottomAndCaption();
      this.fullscreenToggleTarget.classList.add('d-none');
    };

    const hideFn = () => {
      container.classList.remove('d-flex');
      container.classList.add('d-none');
      this.showMobileBannerBottomAndCaption();
      this.fullscreenToggleTarget.classList.remove('d-none');
    };

    return new EmbeddedElementConfig(container, showFn, hideFn);
  }

  createNonImageElement(config, element) {
    return createVideo(element, config.id, 0, 0, 'fullscreen-video');
  }

  handleFullscreenControlsElementsVisibility() {
    this.element.classList.toggle(
      'full-screen-gallery--video',
      this.hasFullscreenThumbnailVideoTarget && this.fullscreenThumbnailVideoTarget.classList.contains('active')
    );
  }
}
